
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  User,
  Plus, 
  Globe, 
  X,
  PlusCircle,
  Link as LinkIcon,
  Layers,
  FileText,
  Video,
  Eye,
  Lock,
  Trash2,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Upload,
  ChevronRight,
  ShieldAlert,
  LogOut,
  Settings,
  Shield,
  ShieldCheck,
  Bell,
  CreditCard,
  CalendarDays,
  Clock,
  ChevronDown,
  Printer,
  FileBarChart,
  BarChart2,
  Fingerprint,
  Check,
  Loader2
} from 'lucide-react';
import { Logo } from '../ui/Logo';
import { GlowingEffect } from '../ui/GlowingEffect';
import { UserRole, Creator, OSContent, Booking } from '../../App';
import { MinimalistDock, DockItem } from '../ui/minimal-dock';
import { AccessGate } from '../access/AccessGate';
import { BuyerView } from '../os/BuyerView';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';

interface AgencyDashboardProps {
  role: UserRole;
  onLogout: () => void;
  creators: Creator[];
  setCreators: React.Dispatch<React.SetStateAction<Creator[]>>;
  content: OSContent[];
  setContent: React.Dispatch<React.SetStateAction<OSContent[]>>;
  selectedCreatorId: string | null;
  setSelectedCreatorId: React.Dispatch<React.SetStateAction<string | null>>;
  bookings: Booking[];
  setBookings: React.Dispatch<React.SetStateAction<Booking[]>>;
}

type ModalType = 'creator' | 'os_content' | 'generate_link' | 'actions' | null;

interface SecureLink {
  id: string;
  creatorId: string;
  assetId: string;
  assetName: string;
  token: string;
  status: 'active' | 'revoked';
  views: number;
}

interface AuditLog {
  id: string;
  created_at: string;
  creator_id: string;
  link_token: string;
  asset_name: string;
  status: string;
  fingerprint: string;
}

function CustomSelect({ options, value, onChange, placeholder = "Select...", className = "" }: { 
  options: { label: string, value: string }[], 
  value: string, 
  onChange: (val: string) => void,
  placeholder?: string,
  className?: string
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(o => o.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div 
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative flex items-center justify-between bg-muted/10 border border-border/20 rounded-2xl py-3.5 px-6 cursor-pointer transition-all hover:bg-muted/20 overflow-hidden group",
          isOpen && "border-primary/40 ring-1 ring-primary/20"
        )}
      >
        <GlowingEffect spread={40} glow={isHovered || isOpen} disabled={false} proximity={80} inactiveZone={0.01} borderWidth={1.5} />
        <span className={cn("text-[10px] font-bold tracking-widest uppercase relative z-10 transition-colors", !selectedOption && "text-muted-foreground/40", isHovered && "text-foreground")}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={cn("size-4 text-muted-foreground/40 transition-transform duration-300 relative z-10", isOpen && "rotate-180")} />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute top-full left-0 right-0 z-[120] mt-1 bg-card/90 backdrop-blur-2xl border border-border/40 rounded-2xl shadow-2xl overflow-hidden p-1.5"
          >
            <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all group",
                    value === option.value ? "bg-primary text-primary-foreground" : "hover:bg-muted/30"
                  )}
                >
                  <span className="text-[10px] font-bold tracking-widest uppercase">{option.label}</span>
                  {value === option.value && <Check size={12} />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function AgencyDashboard({ 
  role, 
  onLogout, 
  creators, 
  setCreators, 
  content, 
  setContent,
  selectedCreatorId,
  setSelectedCreatorId,
  bookings,
  setBookings
}: AgencyDashboardProps) {
  const [activeTab, setActiveTab] = useState<'creators' | 'os' | 'bookings' | 'reports' | 'logs' | 'settings'>('creators');
  const [osStep, setOsStep] = useState<'content' | 'branding' | 'links'>('content');
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPreviewAuthorized, setIsPreviewAuthorized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [links, setLinks] = useState<SecureLink[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [selectedAssetForLink, setSelectedAssetForLink] = useState<string>('all');

  useEffect(() => {
    const fetchLinks = async () => {
      const { data } = await supabase.from('links').select('*');
      if (data) {
        setLinks(data.map(l => ({
          id: l.id,
          creatorId: l.creator_id,
          assetId: l.asset_id,
          assetName: l.asset_name,
          token: l.token,
          status: l.status as any,
          views: l.views || 0
        })));
      }
    };
    fetchLinks();
  }, []);

  // Fetch real-time Audit Logs from Supabase
  useEffect(() => {
    const fetchLogs = async () => {
      const { data } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (data) {
        setAuditLogs(data);
      }
    };

    fetchLogs();
    
    // Set up polling for real-time feel
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, []);

  const selectedCreator = creators.find(c => c.id === selectedCreatorId);
  const creatorAssets = content.filter(item => item.creatorId === selectedCreatorId);

  const dockItems: DockItem[] = [
    { id: 'creators', label: 'Clients', icon: <Users size={20} />, onClick: () => setActiveTab('creators') },
    { id: 'os', label: 'Protection', icon: <Layers size={20} />, onClick: () => setActiveTab('os') },
    { id: 'reports', label: 'Reports', icon: <BarChart2 size={20} />, onClick: () => setActiveTab('reports') },
    { id: 'bookings', label: 'Sessions', icon: <CalendarDays size={20} />, onClick: () => setActiveTab('bookings') },
    { id: 'logs', label: 'Security Logs', icon: <ShieldAlert size={20} />, onClick: () => setActiveTab('logs') },
    { id: 'settings', label: 'Settings', icon: <Settings size={20} />, onClick: () => setActiveTab('settings') },
    { id: 'logout', label: 'Log Out', icon: <LogOut size={20} />, onClick: onLogout },
  ];

  const handlePrintReport = () => {
    window.print();
  };

  const handleAddCreator = async (data: any) => {
    setIsProcessing(true);
    const { data: profileData, error } = await supabase.from('profiles').insert([{
      full_name: data.name,
      community_name: data.communityName || data.name,
      role: 'agency_owner'
    }]).select().single();

    setIsProcessing(false);
    if (error) {
      alert(error.message);
      return;
    }

    const newCreator: Creator = {
      id: profileData.id,
      name: profileData.full_name,
      communityName: profileData.community_name,
      logoUrl: '',
      accentColor: '#3B82F6',
      welcomeText: 'Welcome to your portal.',
      plan: 'Starter'
    };
    setCreators([...creators, newCreator]);
    setSelectedCreatorId(newCreator.id);
    setActiveModal(null);
  };

  const handleAddContent = async (data: any) => {
    if (!selectedCreatorId) return;
    setIsProcessing(true);

    const files = data.files as FileList;
    const newItems: OSContent[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const { data: assetData, error } = await supabase.from('assets').insert([{
        creator_id: selectedCreatorId,
        name: files.length > 1 ? `${data.name} (Part ${i + 1})` : data.name,
        type: data.type,
        file_name: file.name,
        url: data.type === 'Video' ? 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4' : undefined
      }]).select().single();

      if (!error && assetData) {
        newItems.push({
          id: assetData.id,
          creatorId: selectedCreatorId,
          name: assetData.name,
          type: assetData.type as any,
          fileName: assetData.file_name,
          url: assetData.url
        });
      }
    }

    setContent([...content, ...newItems]);
    setIsProcessing(false);
    setActiveModal(null);
  };

  const handleCreateLink = async () => {
    if (!selectedCreatorId) return;
    const asset = creatorAssets.find(a => a.id === selectedAssetForLink) || { name: 'Full Portal Access', id: 'all' };
    
    const token = Math.random().toString(36).substr(2, 12);
    const { data: linkData, error } = await supabase.from('links').insert([{
      creator_id: selectedCreatorId,
      asset_id: asset.id === 'all' ? null : asset.id,
      asset_name: asset.name,
      token: token,
      status: 'active',
      views: 0
    }]).select().single();

    if (error) {
      alert(error.message);
      return;
    }

    const newLink: SecureLink = { 
      id: linkData.id, 
      creatorId: selectedCreatorId, 
      assetId: linkData.asset_id || 'all', 
      assetName: linkData.asset_name,
      token: linkData.token, 
      status: 'active',
      views: 0
    };
    setLinks([...links, newLink]);
  };

  const handleToggleLinkStatus = async (linkId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'revoked' : 'active';
    await supabase.from('links').update({ status: newStatus }).eq('id', linkId);
    setLinks(links.map(l => l.id === linkId ? { ...l, status: newStatus as any } : l));
  };

  const newBookingsCount = bookings.filter(b => b.status === 'new').length;

  return (
    <div className="min-h-screen bg-background font-light">
      <div id="printable-report" className="hidden print:block p-12 bg-white text-black min-h-screen">
        <div className="flex justify-between items-start mb-12">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold tracking-tight">Aure OS Protection Report</h1>
            <p className="text-sm uppercase tracking-widest text-slate-500">Security Audit & Performance Metrics</p>
          </div>
          <div className="text-right">
             <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Date Generated</p>
             <p className="text-lg font-medium">{new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-12 mb-16 border-y border-slate-100 py-12">
           <div className="space-y-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Protected Brand</p>
                <p className="text-2xl font-light">{selectedCreator?.name || 'All Clients'}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Community Infrastructure</p>
                <p className="text-lg font-light">{selectedCreator?.communityName || 'Global Deployment'}</p>
              </div>
           </div>
           <div className="grid grid-cols-2 gap-8">
              <div className="space-y-1">
                <p className="text-3xl font-bold text-slate-900">{content.filter(i => i.creatorId === selectedCreatorId).length}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assets Secured</p>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-emerald-600">Active</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Protection Status</p>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-slate-900">{links.reduce((acc, l) => acc + l.views, 0)}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Portal Accesses</p>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-slate-900">${creators.length * 499}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Revenue Recovered</p>
              </div>
           </div>
        </div>

        <div className="space-y-8">
           <div className="flex items-center gap-3">
             <ShieldCheck className="size-6 text-emerald-600" />
             <h2 className="text-xl font-bold tracking-tight">Security Audit Trace (Recent Access)</h2>
           </div>
           <div className="border border-slate-100 rounded-2xl overflow-hidden">
             <table className="w-full text-left">
               <thead className="bg-slate-50 border-b border-slate-100">
                 <tr>
                   <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Timestamp</th>
                   <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Link Token</th>
                   <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Asset Targeted</th>
                   <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</th>
                 </tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                 {auditLogs.slice(0, 10).map((l) => (
                   <tr key={l.id}>
                     <td className="p-6 text-sm text-slate-500 font-mono">[{new Date(l.created_at).toLocaleTimeString()}]</td>
                     <td className="p-6 text-sm font-medium">{l.link_token}</td>
                     <td className="p-6 text-sm">{l.asset_name}</td>
                     <td className="p-6">
                       <span className="text-[10px] font-bold uppercase px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full">{l.status}</span>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
        </div>

        <div className="mt-auto pt-20 flex justify-between items-end">
           <div className="space-y-2">
             <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Generated via Aure OS Encryption Engine</p>
             <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Confidential Agency Report</p>
           </div>
           <div className="size-16 grayscale opacity-20">
             <Logo />
           </div>
        </div>
      </div>

      <AnimatePresence>
        {isPreviewing && selectedCreator && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-background print:hidden"
          >
            {!isPreviewAuthorized ? (
              <AccessGate 
                onBack={() => { setIsPreviewing(false); setIsPreviewAuthorized(false); }} 
                onGrantAccess={() => setIsPreviewAuthorized(true)}
                isAdmin={true}
                branding={{
                  creatorName: selectedCreator.name,
                  logoUrl: selectedCreator.logoUrl,
                  accentColor: selectedCreator.accentColor
                }}
              />
            ) : (
              <div className="relative h-full w-full">
                <button 
                  onClick={() => { setIsPreviewing(false); setIsPreviewAuthorized(false); }}
                  className="fixed top-8 right-8 z-[210] flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-widest uppercase shadow-2xl hover:scale-95 transition-all"
                >
                  <X size={14} /> Close Preview
                </button>
                <BuyerView 
                  onExit={() => setIsPreviewAuthorized(false)} 
                  branding={{
                    creatorName: selectedCreator.name,
                    logoUrl: selectedCreator.logoUrl,
                    accentColor: selectedCreator.accentColor
                  }}
                  content={content.filter(c => c.creatorId === selectedCreatorId)}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeModal && (
          <div className="print:hidden">
            <ModalWrapper onClose={() => !isProcessing && setActiveModal(null)}>
              {activeModal === 'actions' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-light">Action Center</h2>
                  <div className="grid gap-4">
                    <ActionButton icon={<User size={18} />} label="Add Client" sub="Register a new brand" onClick={() => setActiveModal('creator')} />
                    <ActionButton icon={<Upload size={18} />} label="Upload Products" sub="Secure multiple assets" onClick={() => setActiveModal('os_content')} />
                  </div>
                </div>
              )}
              {activeModal === 'creator' && (
                <AssetForm title="Add Brand" fields={[{ name: 'name', label: 'Brand Name', icon: <User size={16} /> }, { name: 'communityName', label: 'Community Name', icon: <Globe size={16} /> }]} buttonLabel="Register Brand" onComplete={handleAddCreator} isProcessing={isProcessing} />
              )}
              {activeModal === 'os_content' && (
                <AssetForm title="Secure Content" fields={[{ name: 'name', label: 'Product Base Name', icon: <FileText size={16} /> }, { name: 'type', label: 'Type', options: ['Video', 'PDF', 'Text'], icon: <Video size={16} /> }, { name: 'files', label: 'Choose Files (Multiple)', type: 'file', icon: <Upload size={16} />, multiple: true }]} buttonLabel="Upload & Secure Batch" onComplete={handleAddContent} isProcessing={isProcessing} />
              )}
            </ModalWrapper>
          </div>
        )}
      </AnimatePresence>

      <div className="print:hidden">
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/10 bg-background/50 backdrop-blur-xl h-20 flex items-center justify-between px-8">
          <Logo />
          <div className="flex items-center gap-6">
            {newBookingsCount > 0 && (
              <motion.button 
                onClick={() => setActiveTab('bookings')}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 border border-primary/20 text-primary animate-pulse"
              >
                <Bell size={14} />
                <span className="text-[10px] font-bold tracking-widest uppercase">{newBookingsCount} New Session{newBookingsCount > 1 ? 's' : ''}</span>
              </motion.button>
            )}
            {selectedCreator && (
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setIsPreviewing(true)}
                  className="group relative size-10 flex items-center justify-center rounded-xl bg-muted/20 border border-border/40 hover:border-primary/40 transition-all overflow-hidden"
                  title="Preview Portal"
                >
                  <GlowingEffect spread={30} glow={true} disabled={false} proximity={50} inactiveZone={0.01} borderWidth={1.5} />
                  <Eye size={18} className="text-muted-foreground group-hover:text-primary transition-colors relative z-10" />
                </button>
                <div className="px-4 py-2 rounded-2xl bg-muted/20 border border-border/40 flex items-center gap-3">
                  <div className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-bold tracking-widest uppercase">Brand: {selectedCreator.name}</span>
                </div>
              </div>
            )}
            <button onClick={() => setActiveModal('actions')} className="py-2.5 px-6 rounded-full bg-primary text-primary-foreground text-[9px] font-bold tracking-[0.3em] uppercase transition-all hover:scale-[0.98]">
              New Action
            </button>
          </div>
        </header>

        <main className="max-w-7xl mx-auto pt-32 pb-48 px-8">
          {activeTab === 'creators' && (
            <div className="space-y-12">
              <header className="flex justify-between items-end">
                <h1 className="text-5xl tracking-tighter font-light">Client <span className="text-muted-foreground/20 italic">Portfolio</span></h1>
                <button onClick={() => setActiveModal('creator')} className="size-12 rounded-full border border-border/40 flex items-center justify-center hover:text-primary transition-all"><Plus size={24} /></button>
              </header>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {creators.map(c => (
                  <CreatorCard key={c.id} creator={c} isActive={selectedCreatorId === c.id} onClick={() => { setSelectedCreatorId(c.id); setActiveTab('os'); }} />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-12">
              <header className="flex items-center justify-between">
                <h1 className="text-5xl tracking-tighter font-light">Access <span className="text-muted-foreground/20 italic">Reports</span></h1>
                <button 
                  onClick={handlePrintReport}
                  className="flex items-center gap-3 px-6 py-2.5 rounded-2xl bg-muted/10 border border-border/40 hover:border-primary/40 transition-all group"
                >
                  <Printer size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground group-hover:text-primary transition-colors">Print Complete Audit</span>
                </button>
              </header>
              <div className="grid gap-6">
                {links.length > 0 ? links.map(link => (
                  <div key={link.id} className="p-8 rounded-[40px] border border-border/40 bg-card/40 flex flex-col md:flex-row md:items-center justify-between gap-6 group">
                    <div className="flex items-center gap-6">
                      <div className="size-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary relative overflow-hidden">
                        <BarChart2 size={24} />
                      </div>
                      <div>
                        <h4 className="text-xl font-medium">{link.assetName}</h4>
                        <p className="text-[10px] font-mono text-muted-foreground/40 uppercase tracking-widest">Token: {link.token}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-12">
                      <div className="text-center">
                        <p className="text-2xl font-light tabular-nums">{link.views}</p>
                        <p className="text-[9px] font-bold tracking-widest uppercase text-muted-foreground/40">Total Views</p>
                      </div>
                      <div className="text-center">
                        <p className={cn("text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full", link.status === 'active' ? "bg-emerald-500/10 text-emerald-500" : "bg-destructive/10 text-destructive")}>
                          {link.status}
                        </p>
                        <p className="text-[9px] font-bold tracking-widest uppercase text-muted-foreground/40 mt-1">Status</p>
                      </div>
                      <button onClick={() => handleToggleLinkStatus(link.id, link.status)} className="p-4 rounded-2xl bg-muted/20 hover:bg-muted/40 transition-colors">
                        <Lock size={18} className={link.status === 'active' ? 'text-muted-foreground' : 'text-primary'} />
                      </button>
                    </div>
                  </div>
                )) : (
                   <div className="h-64 rounded-[40px] border border-dashed border-border/40 flex flex-col items-center justify-center text-center p-12 space-y-4">
                    <FileBarChart size={40} className="text-muted-foreground/20" />
                    <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40">No link data available for audit...</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'os' && selectedCreator && (
            <div className="space-y-12">
              <header className="flex items-center justify-between border-b border-border/10 pb-6">
                <div className="flex items-center gap-8">
                  {['content', 'branding', 'links'].map((s: any) => (
                    <button key={s} onClick={() => setOsStep(s)} className={cn("text-[10px] font-bold tracking-[0.2em] uppercase transition-all", osStep === s ? "text-primary" : "text-muted-foreground")}>{s === 'links' ? 'Access Links' : s}</button>
                  ))}
                </div>
                
                <button 
                  onClick={handlePrintReport}
                  className="flex items-center gap-3 px-6 py-2.5 rounded-2xl bg-muted/10 border border-border/40 hover:border-primary/40 transition-all group"
                >
                  <Printer size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground group-hover:text-primary transition-colors">Export PDF Report</span>
                </button>
              </header>

              <AnimatePresence mode="wait">
                {osStep === 'content' && (
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <button onClick={() => setActiveModal('os_content')} className="h-[200px] rounded-[40px] border-2 border-dashed border-border/40 flex flex-col items-center justify-center gap-4 hover:border-primary/40 transition-all group">
                        <Plus className="group-hover:scale-110 transition-transform" />
                        <span className="text-[10px] font-bold tracking-widest uppercase">Secure New Content</span>
                      </button>
                      {content.filter(item => item.creatorId === selectedCreatorId).map(item => (
                        <div key={item.id} className="p-8 rounded-[40px] border border-border/40 bg-card/40 flex flex-col justify-between group">
                          <div className="flex justify-between items-start">
                            <div className="p-3 bg-muted/20 rounded-xl group-hover:bg-primary/10 transition-colors">{item.type === 'Video' ? <Video size={18}/> : <FileText size={18}/>}</div>
                            <Trash2 size={14} className="text-muted-foreground/40 hover:text-destructive cursor-pointer" onClick={async () => {
                              await supabase.from('assets').delete().eq('id', item.id);
                              setContent(content.filter(i => i.id !== item.id));
                            }} />
                          </div>
                          <h4 className="text-lg font-medium mt-4">{item.name}</h4>
                          <p className="text-[10px] font-mono text-muted-foreground/40 truncate">{item.fileName || 'Protected Asset'}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-center"><button onClick={() => setOsStep('branding')} className="group flex items-center gap-3 px-10 py-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-widest uppercase transition-all hover:scale-95">Set Portal Branding <ChevronRight size={14} /></button></div>
                  </motion.div>
                )}

                {osStep === 'branding' && (
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                     <div className="space-y-8">
                        <div className="space-y-2"><p className="text-[10px] font-bold tracking-widest text-primary uppercase">Identity</p><h3 className="text-2xl font-light">Custom White-Labeling</h3></div>
                        <div className="space-y-4">
                          <BrandingInput label="Primary Theme Color" type="color" value={selectedCreator.accentColor} onChange={async (v: string) => {
                            await supabase.from('profiles').update({ accent_color: v }).eq('id', selectedCreatorId);
                            setCreators(creators.map(c => c.id === selectedCreatorId ? {...c, accentColor: v} : c));
                          }} />
                          <BrandingInput label="Welcome Greeting" value={selectedCreator.welcomeText} onChange={async (v: string) => {
                            await supabase.from('profiles').update({ welcome_text: v }).eq('id', selectedCreatorId);
                            setCreators(creators.map(c => c.id === selectedCreatorId ? {...c, welcomeText: v} : c));
                          }} />
                          <BrandingInput label="Brand Logo URL" value={selectedCreator.logoUrl} onChange={async (v: string) => {
                            await supabase.from('profiles').update({ logo_url: v }).eq('id', selectedCreatorId);
                            setCreators(creators.map(c => c.id === selectedCreatorId ? {...c, logoUrl: v} : c));
                          }} />
                        </div>
                        <button onClick={() => setOsStep('links')} className="w-full py-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-widest uppercase transition-all hover:scale-95">Generate Access Links</button>
                     </div>
                     <div className="p-10 rounded-[48px] border border-border/40 bg-card/20 flex flex-col items-center justify-center text-center space-y-6">
                        <div className="size-20 rounded-3xl bg-muted/20 border border-border/40 flex items-center justify-center overflow-hidden">
                          {selectedCreator.logoUrl ? <img src={selectedCreator.logoUrl} className="size-full object-contain" /> : <Logo />}
                        </div>
                        <h4 className="text-xl">{selectedCreator.communityName}</h4>
                        <div className="w-40 h-1 rounded-full bg-muted/20 overflow-hidden"><div className="h-full" style={{ width: '60%', backgroundColor: selectedCreator.accentColor }} /></div>
                        <p className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase">Real-time Portal Preview</p>
                     </div>
                  </motion.div>
                )}

                {osStep === 'links' && (
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-8">
                    <div className="p-10 rounded-[40px] border border-border/40 bg-card/40 flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div className="max-w-md">
                        <h3 className="text-2xl font-light">Secure Portal Links</h3>
                        <p className="text-sm text-muted-foreground font-light">Generate unique, protected links for your members. Links now include the course context in the URL.</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <CustomSelect 
                          placeholder="Full Portal Access"
                          className="w-64"
                          value={selectedAssetForLink}
                          onChange={setSelectedAssetForLink}
                          options={[
                            { label: 'Full Portal Access', value: 'all' },
                            ...creatorAssets.map(asset => ({ label: asset.name, value: asset.id }))
                          ]}
                        />
                        <button onClick={handleCreateLink} className="px-8 py-3 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-widest uppercase transition-all hover:scale-95">New Access Link</button>
                      </div>
                    </div>
                    <div className="grid gap-4">
                      {links.filter(l => l.creatorId === selectedCreatorId).map(link => {
                        const courseParam = link.assetName.replace(/\s+/g, '-').toLowerCase();
                        const fullLink = `${window.location.origin}/portal?course=${courseParam}&access=${link.token}`;
                        return (
                          <div key={link.id} className="p-6 rounded-3xl bg-muted/5 border border-border/40 flex items-center justify-between">
                             <div className="flex items-center gap-6">
                                <div className="size-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500"><LinkIcon size={18}/></div>
                                <div>
                                   <p className="text-sm font-mono truncate max-w-sm">/portal?course={courseParam}&access={link.token}</p>
                                   <div className="flex items-center gap-3 mt-1">
                                      <p className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase">Target: {link.assetName}</p>
                                      <span className="text-[7px] text-muted-foreground/30">•</span>
                                      <p className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase">Status: {link.status}</p>
                                   </div>
                                </div>
                             </div>
                             <div className="flex gap-2">
                                <button onClick={() => navigator.clipboard.writeText(fullLink)} className="p-3 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors"><Copy size={14}/></button>
                                <button onClick={() => handleToggleLinkStatus(link.id, link.status)} className={cn("p-3 rounded-xl transition-all", link.status === 'active' ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-500")} title="Toggle Link Status"><Lock size={14}/></button>
                             </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-12">
              <header className="space-y-2">
                <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-primary">Inbound Requests</p>
                <h1 className="text-5xl tracking-tighter font-light">Scheduled <span className="text-muted-foreground/20 italic">Sessions</span></h1>
              </header>

              <div className="grid gap-6">
                {bookings.length > 0 ? (
                  bookings.map((booking) => (
                    <div key={booking.id} className="p-8 rounded-[40px] border border-border/40 bg-card/40 flex flex-col md:flex-row md:items-center justify-between gap-6 group">
                      <div className="flex items-center gap-6">
                        <div className="size-14 rounded-2xl bg-foreground/5 flex items-center justify-center text-primary relative overflow-hidden">
                          <GlowingEffect spread={40} glow={true} disabled={false} proximity={60} inactiveZone={0} borderWidth={1} />
                          <User size={24} />
                        </div>
                        <div>
                          <h4 className="text-xl font-medium">{booking.name}</h4>
                          <p className="text-sm text-muted-foreground font-light">{booking.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-12">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase text-muted-foreground/40">
                            <CalendarDays size={12} /> Date
                          </div>
                          <p className="text-sm font-medium">{booking.date}</p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {booking.status === 'new' && (
                            <button 
                              onClick={async () => {
                                await supabase.from('bookings').update({ status: 'reviewed' }).eq('id', booking.id);
                                setBookings(prev => prev.map(b => b.id === booking.id ? {...b, status: 'reviewed'} : b));
                              }}
                              className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-widest uppercase hover:scale-95 transition-all"
                            >
                              Accept Session
                            </button>
                          )}
                          <button onClick={async () => {
                            await supabase.from('bookings').delete().eq('id', booking.id);
                            setBookings(prev => prev.filter(b => b.id !== booking.id));
                          }} className="p-3 rounded-xl bg-muted/20 hover:bg-muted/40 transition-colors text-muted-foreground"><X size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="h-64 rounded-[40px] border border-dashed border-border/40 flex flex-col items-center justify-center text-center p-12 space-y-4">
                    <Clock size={40} className="text-muted-foreground/20" />
                    <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40">Waiting for first session...</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'logs' && <FingerprintLogsView logs={auditLogs} />}
          {activeTab === 'settings' && <AgencySettingsView creators={creators} />}
        </main>

        <MinimalistDock items={dockItems} activeId={activeTab} />
      </div>
    </div>
  );
}

const planPrices = {
  'Starter': 499,
  'Growth': 999,
  'Enterprise': 1999
};

function AgencySettingsView({ creators }: { creators: Creator[] }) {
  return (
    <div className="space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="space-y-2">
        <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-primary">Configuration</p>
        <h2 className="text-4xl font-light tracking-tighter">System <span className="text-muted-foreground/20 italic">Settings</span></h2>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
           <SettingsSection title="Profile & Identity" icon={User}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-8">
                 <BrandingInput label="Agency Name" value="Aure OS Master" />
                 <BrandingInput label="Contact Email" value="zachmiles413@gmail.com" />
              </div>
           </SettingsSection>

           <SettingsSection title="Active Brand Subscriptions" icon={Users}>
              <div className="divide-y divide-border/10">
                {creators.length > 0 ? (
                  creators.map(creator => (
                    <div key={creator.id} className="p-8 flex items-center justify-between hover:bg-muted/5 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                          {creator.name[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{creator.name}</p>
                          <p className="text-[9px] font-bold tracking-widest uppercase text-muted-foreground/40">{creator.plan} Plan</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-light tabular-nums">${planPrices[creator.plan] || 499}/mo</p>
                        <p className="text-[8px] font-bold tracking-[0.2em] uppercase text-emerald-500">Active</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-12 text-center text-muted-foreground/40 text-[10px] font-bold tracking-widest uppercase">No active subscriptions</div>
                )}
              </div>
           </SettingsSection>
        </div>

        <div className="space-y-8">
           <SettingsSection title="Agency Revenue" icon={CreditCard}>
              <div className="p-8 space-y-6">
                 <div className="text-center space-y-2">
                    <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground/40">Total MRR</p>
                    <h3 className="text-4xl font-light tracking-tighter">
                      ${creators.reduce((acc, c) => acc + (planPrices[c.plan] || 499), 0)}
                    </h3>
                 </div>
                 <div className="h-px w-full bg-border/10" />
                 <button className="w-full py-4 rounded-2xl bg-muted/20 border border-border/40 text-[10px] font-bold tracking-widest uppercase hover:bg-muted/40 transition-all">Payout History</button>
              </div>
           </SettingsSection>

           <SettingsSection title="Security & Notifications" icon={Bell}>
              <div className="p-8 space-y-4">
                 <ToggleItem label="Security Alerts" active />
                 <ToggleItem label="New Brand Signup" active />
                 <ToggleItem label="System Sync Alerts" />
              </div>
           </SettingsSection>
        </div>
      </div>
    </div>
  );
}

function SettingsSection({ title, icon: Icon, children }: any) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div 
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)}
      className="rounded-[40px] border border-border/40 bg-card/30 overflow-hidden relative"
    >
       <GlowingEffect spread={60} glow={isHovered} disabled={false} proximity={120} inactiveZone={0.01} borderWidth={1} />
       <div className="p-6 border-b border-border/10 bg-muted/10 flex items-center gap-3">
          <div className="p-2 bg-foreground/5 rounded-xl text-muted-foreground"><Icon size={16}/></div>
          <span className="text-[11px] font-bold tracking-[0.2em] uppercase">{title}</span>
       </div>
       {children}
    </div>
  );
}

function ToggleItem({ label, active }: { label: string, active?: boolean }) {
  return (
    <div className="flex items-center justify-between">
       <span className="text-[11px] text-muted-foreground font-light">{label}</span>
       <div className={cn("w-10 h-5 rounded-full transition-all flex items-center p-1", active ? "bg-emerald-500/40" : "bg-muted/40")}>
          <div className={cn("size-3 rounded-full transition-all", active ? "bg-emerald-500 translate-x-5" : "bg-muted-foreground/40 translate-x-0")} />
       </div>
    </div>
  );
}

function CreatorCard({ creator, isActive, onClick }: any) {
  return (
    <div onClick={onClick} className={cn("p-10 rounded-[48px] border transition-all cursor-pointer relative overflow-hidden group", isActive ? "border-primary bg-primary/5 shadow-2xl" : "border-border/40 bg-card/40 hover:border-primary/20 hover:scale-[1.02]")}>
      <div className="size-16 rounded-2xl bg-foreground/5 flex items-center justify-center text-2xl font-light text-primary mb-6 transition-all group-hover:scale-110">{creator.name[0]}</div>
      <h4 className="text-2xl font-light tracking-tight">{creator.name}</h4>
      <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase mt-2">{creator.communityName}</p>
    </div>
  );
}

function BrandingInput({ label, value, onChange, type = 'text' }: any) {
  return (
    <div className="space-y-1.5 w-full">
      <label className="text-[9px] font-bold tracking-widest text-muted-foreground/40 uppercase ml-1">{label}</label>
      <input type={type} defaultValue={value} onChange={(e) => onChange?.(e.target.value)} className="w-full bg-muted/10 border border-border/20 rounded-2xl py-4 px-6 outline-none focus:border-primary/40 transition-all text-sm font-light" />
    </div>
  );
}

function ModalWrapper({ children, onClose }: any) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[100] bg-background/60 backdrop-blur-md" />
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 pointer-events-none">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="w-full max-w-lg pointer-events-auto bg-card p-10 rounded-[40px] border border-border/40 shadow-2xl relative">
          <button onClick={onClose} className="absolute right-8 top-8 text-muted-foreground hover:text-foreground transition-colors z-10"><X size={20}/></button>
          {children}
        </motion.div>
      </div>
    </>
  );
}

function ActionButton({ icon, label, sub, onClick }: any) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-6 p-6 rounded-3xl border border-border/40 bg-muted/5 hover:bg-primary hover:text-primary-foreground transition-all duration-500 overflow-hidden group">
      <div className="size-12 rounded-2xl bg-foreground/5 flex items-center justify-center group-hover:bg-background/20 transition-colors">{icon}</div>
      <div className="text-left flex-1"><p className="text-sm font-medium">{label}</p><p className="text-[10px] tracking-widest uppercase opacity-40">{sub}</p></div>
      <PlusCircle size={18} className="opacity-0 group-hover:opacity-100 transition-all" />
    </button>
  );
}

function AssetForm({ title, fields, buttonLabel, onComplete, isProcessing }: any) {
  const [values, setValues] = useState<any>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-light">{title}</h2>
      <div className="space-y-4">
        {fields.map((f: any) => (
          <div key={f.name} className="space-y-1.5">
            <label className="text-[9px] font-bold tracking-widest text-muted-foreground/40 uppercase ml-1">{f.label}</label>
            {f.type === 'file' ? (
              <div onClick={() => fileInputRef.current?.click()} className="w-full bg-muted/10 border border-border/20 rounded-2xl py-4 px-6 text-sm text-muted-foreground cursor-pointer hover:bg-muted/20 transition-all">
                <input ref={fileInputRef} type="file" multiple={f.multiple} className="hidden" onChange={(e) => setValues({...values, [f.name]: e.target.files})} />
                {values[f.name] ? `${values[f.name].length} file(s) selected` : 'Choose from local...'}
              </div>
            ) : f.options ? (
              <CustomSelect 
                options={f.options.map((o: string) => ({ label: o, value: o }))} 
                value={values[f.name] || ""} 
                onChange={(v) => setValues({...values, [f.name]: v})} 
                placeholder="Select type..."
              />
            ) : (
              <input type="text" onChange={(e) => setValues({...values, [f.name]: e.target.value})} className="w-full bg-muted/10 border border-border/20 rounded-2xl py-4 px-6 outline-none text-sm font-light" />
            )}
          </div>
        ))}
      </div>
      <button 
        onClick={() => onComplete(values)} 
        disabled={isProcessing}
        className="group relative w-full py-5 rounded-full bg-foreground text-background text-[11px] font-bold tracking-[0.4em] uppercase transition-all hover:scale-[0.98] shadow-2xl flex items-center justify-center gap-3"
      >
        <GlowingEffect spread={60} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={2} variant="white" />
        {isProcessing ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            <span>Processing Batch...</span>
          </>
        ) : (
          <span>{buttonLabel}</span>
        )}
      </button>
    </div>
  );
}

function FingerprintLogsView({ logs }: { logs: AuditLog[] }) {
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4 bg-destructive/5 border border-destructive/20 p-6 rounded-3xl">
         <AlertTriangle className="text-brand-gold" />
         <div><p className="text-sm font-medium">Automatic Guard Enabled</p><p className="text-[10px] tracking-widest uppercase text-muted-foreground/40 font-bold">Real-time security analysis active.</p></div>
      </div>
      <div className="rounded-[40px] border border-border/40 overflow-hidden bg-card/30 font-mono">
        <div className="p-8 bg-muted/20 border-b border-border/10 flex items-center justify-between"><span className="text-[10px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40">Security Audit Trace</span></div>
        <div className="divide-y divide-border/10">
          {logs.length > 0 ? logs.map((log) => (
            <div key={log.id} className="p-6 flex items-center justify-between hover:bg-muted/10 transition-colors">
              <div className="flex items-center gap-6">
                <div className="text-[10px] text-muted-foreground/20 font-light">[{new Date(log.created_at).toLocaleTimeString()}]</div>
                <div className="text-[11px] text-foreground/80">
                  <span className="text-primary">TOKEN:{log.link_token.slice(0, 8)}...</span> • ASSET: <span className="font-bold">{log.asset_name}</span> • ID: <span className="opacity-40">{log.fingerprint.slice(-8)}</span>
                </div>
              </div>
              <span className="text-[9px] font-bold tracking-widest uppercase text-emerald-500 flex items-center gap-2"><CheckCircle2 size={12} /> {log.status}</span>
            </div>
          )) : (
            <div className="p-12 text-center text-muted-foreground/20 text-[10px] font-bold tracking-widest uppercase italic">No active sessions detected...</div>
          )}
        </div>
      </div>
    </div>
  );
}
